import { doc, getDoc, getDocFromServer, onSnapshot, setDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import type { CycleRing } from '@/lib/cycleTypes';

const getDb = () => db;

export type CycleGroupData = {
  id: string;
  name: string;
  description?: string;
  rings: CycleRing[];
  hiddenRings?: string[];
  visibleRingCount?: number;
};

const LOCAL_GROUPS_KEY = 'cycle-groups';
const SELECTED_GROUP_KEY = 'cycle-groups:selected';
const GROUPS_COLLECTION = 'cycleGroups';

const shouldDisableFirestore = () =>
  typeof window !== 'undefined' && window.localStorage?.getItem('disableFirestore') === '1';

const getGroupDocId = (viewId?: string | null) => `cycle-groups-${viewId ?? 'default'}`;
const getGroupDocRef = (viewId?: string | null) => {
  const db = getDb();
  if (!db) throw new Error('Firestore is not configured.');
  return doc(db, GROUPS_COLLECTION, getGroupDocId(viewId));
};

const sanitizeGroups = (groups: CycleGroupData[]): CycleGroupData[] =>
  groups
    .filter(group => group && group.id)
    .map((group) => ({
      id: group.id,
      name: group?.name ?? 'Unnamed Group',
      description: group?.description,
      rings: group?.rings ?? [],
      hiddenRings: (group?.hiddenRings ?? []),
      visibleRingCount: group?.visibleRingCount,
    }));

type GroupListener = (groups: CycleGroupData[]) => void;
type SelectedListener = (id: string | null) => void;

const _selectedListeners: Record<string, SelectedListener[]> = {};
const _groupListeners: Record<string, GroupListener[]> = {};

const getGroupsKey = (viewId?: string | null): string =>
  viewId ? `${LOCAL_GROUPS_KEY}:${viewId}` : LOCAL_GROUPS_KEY;

const getSelectedKey = (viewId?: string | null): string =>
  viewId ? `${SELECTED_GROUP_KEY}:${viewId}` : SELECTED_GROUP_KEY;

const loadLegacyGroups = (): CycleGroupData[] => {
  try {
    const stored = localStorage.getItem(LOCAL_GROUPS_KEY);
    if (!stored) return [];
    const parsed = JSON.parse(stored) as CycleGroupData[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const loadGroupsWithMigration = (viewId?: string | null): CycleGroupData[] => {
  const current = loadLocalGroups(viewId);
  if (viewId && current.length === 0) {
    const legacy = loadLegacyGroups();
    if (legacy.length) {
      saveLocalGroups(viewId, legacy);
      try {
        localStorage.removeItem(LOCAL_GROUPS_KEY);
      } catch {
        // ignore storage errors
      }
      return legacy;
    }
  }
  return current;
};

const loadLocalGroups = (viewId?: string | null): CycleGroupData[] => {
  try {
    const stored = localStorage.getItem(getGroupsKey(viewId));
    if (!stored) return [];
    const parsed = JSON.parse(stored) as CycleGroupData[];
    return Array.isArray(parsed) ? sanitizeGroups(parsed) : [];
  } catch {
    return [];
  }
};

const notifyGroupListeners = (viewId: string | null | undefined, groups: CycleGroupData[]) => {
  const key = getGroupsKey(viewId);
  const listeners = _groupListeners[key] ?? [];
  listeners.forEach((cb) => {
    try { cb(groups); } catch { void 0; }
  });
};

const logFirestoreWrite = (detail: Record<string, unknown>) => {
  try {
    console.info('[firestore:write] cycleGroupStore', detail);
  } catch {
    // ignore
  }
};

const saveLocalGroups = (viewId: string | null | undefined, groups: CycleGroupData[]) => {
  const safeGroups = sanitizeGroups(groups);
  try {
    localStorage.setItem(getGroupsKey(viewId), JSON.stringify(safeGroups));
  } catch {
    // ignore storage errors
  }
  notifyGroupListeners(viewId, safeGroups);
};

const saveLocalSelected = (viewId: string | null | undefined, id: string | null) => {
  const key = getSelectedKey(viewId);
  try {
    if (id === null) localStorage.removeItem(key);
    else localStorage.setItem(key, id);
  } catch {
    // ignore
  }
  (_selectedListeners[key] ?? []).forEach((cb) => {
    try { cb(id); } catch { void 0; }
  });
};

const loadRemoteGroups = async (viewId?: string | null): Promise<CycleGroupData[]> => {
  const docRef = getGroupDocRef(viewId);
  try {
    const snapshot = await getDocFromServer(docRef);
    if (snapshot.exists()) return sanitizeGroups((snapshot.data().groups ?? []) as CycleGroupData[]);
  } catch {
    const snapshot = await getDoc(docRef);
    if (snapshot.exists()) return sanitizeGroups((snapshot.data().groups ?? []) as CycleGroupData[]);
  }
  return [];
};

export async function loadCycleGroups(viewId?: string | null): Promise<CycleGroupData[]> {
  const db = getDb();
  if (!db || shouldDisableFirestore()) {
    return loadGroupsWithMigration(viewId);
  }
  const remote = await loadRemoteGroups(viewId);
  if (remote.length) saveLocalGroups(viewId, remote);
  return remote.length ? remote : loadGroupsWithMigration(viewId);
}

export async function saveCycleGroup(viewId: string | null | undefined, group: CycleGroupData): Promise<void> {
  const groups = loadLocalGroups(viewId);
  const idx = groups.findIndex((g) => g.id === group.id);
  if (idx >= 0) groups[idx] = group;
  else groups.push(group);
  saveLocalGroups(viewId, groups);

  const db = getDb();
  if (!db || shouldDisableFirestore()) return;
  try {
    logFirestoreWrite({ viewId: viewId ?? 'default', action: 'saveGroup', count: groups.length });
    await setDoc(getGroupDocRef(viewId), { groups: sanitizeGroups(groups) }, { merge: true });
  } catch {
    // ignore network errors but keep local state
  }
}

export async function deleteCycleGroup(viewId: string | null | undefined, id: string): Promise<void> {
  const groups = loadLocalGroups(viewId).filter((g) => g.id !== id);
  saveLocalGroups(viewId, groups);
  const db = getDb();
  if (!db || shouldDisableFirestore()) return;
  try {
    logFirestoreWrite({ viewId: viewId ?? 'default', action: 'deleteGroup', count: groups.length });
    await setDoc(getGroupDocRef(viewId), { groups: sanitizeGroups(groups) }, { merge: true });
  } catch {
    // ignore network errors
  }
}

export function subscribeCycleGroups(
  viewId: string | null | undefined,
  onData: (groups: CycleGroupData[]) => void,
  _onError?: (error: Error) => void
): (() => void) | null {
  const key = getGroupsKey(viewId);
  try {
    onData(loadGroupsWithMigration(viewId));
  } catch {
    onData([]);
  }
  _groupListeners[key] = _groupListeners[key] ?? [];
  _groupListeners[key].push(onData);

  const db = getDb();
  if (!db || shouldDisableFirestore()) {
    return () => {
      _groupListeners[key] = (_groupListeners[key] ?? []).filter((cb) => cb !== onData);
    };
  }

  const docRef = getGroupDocRef(viewId);
  const unsubscribe = onSnapshot(
    docRef,
    (snapshot) => {
      if (!snapshot.exists()) return;
      const groups = sanitizeGroups((snapshot.data().groups ?? []) as CycleGroupData[]);
      saveLocalGroups(viewId, groups);
    },
    () => {
      // ignore errors; local listeners stay active
    }
  );

  return () => {
    _groupListeners[key] = (_groupListeners[key] ?? []).filter((cb) => cb !== onData);
    unsubscribe();
  };
}

export function loadSelectedGroupId(viewId?: string | null): string | null {
  try {
    const current = localStorage.getItem(getSelectedKey(viewId));
    if (current || !viewId) return current;
    const legacy = localStorage.getItem(SELECTED_GROUP_KEY);
    if (!legacy) return null;
    try {
      localStorage.setItem(getSelectedKey(viewId), legacy);
      localStorage.removeItem(SELECTED_GROUP_KEY);
    } catch {
      // ignore storage errors
    }
    return legacy;
  } catch {
    return null;
  }
}

export function saveSelectedGroupId(viewId: string | null | undefined, id: string | null): void {
  saveLocalSelected(viewId, id);
  const db = getDb();
  if (!db || shouldDisableFirestore()) return;
  try {
    const payload = id === null ? { selectedGroupId: null } : { selectedGroupId: id };
    logFirestoreWrite({ viewId: viewId ?? 'default', action: 'setSelectedGroup', selected: id });
    void setDoc(getGroupDocRef(viewId), payload, { merge: true });
  } catch {
    // ignore
  }
}

export function subscribeSelectedGroup(
  viewId: string | null | undefined,
  cb: (id: string | null) => void
): () => void {
  const key = getSelectedKey(viewId);
  try {
    cb(loadSelectedGroupId(viewId));
  } catch {
    void 0;
  }
  _selectedListeners[key] = _selectedListeners[key] ?? [];
  _selectedListeners[key].push(cb);
  const db = getDb();
  if (!db || shouldDisableFirestore()) {
    return () => {
      _selectedListeners[key] = (_selectedListeners[key] ?? []).filter((c) => c !== cb);
    };
  }

  const unsubscribe = onSnapshot(
    getGroupDocRef(viewId),
    (snapshot) => {
      if (!snapshot.exists()) return;
      const selected = (snapshot.data() as { selectedGroupId?: string | null }).selectedGroupId ?? null;
      saveLocalSelected(viewId, selected);
    },
    () => {
      // ignore errors; rely on local listeners
    }
  );

  return () => {
    _selectedListeners[key] = (_selectedListeners[key] ?? []).filter((c) => c !== cb);
    unsubscribe();
  };
}
